import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Plus, Upload, Download, Search, Filter, RefreshCw, Edit, Trash2, 
  Pause, Play, Copy, Star, StarOff, Eye, EyeOff, MoreVertical,
  CheckCircle, XCircle, Clock, AlertCircle, ShoppingCart, Archive,
  ChevronDown, GripVertical, Image, MapPin, Calendar, TrendingUp,
  Phone, Mail, Building, FileText, X
} from 'lucide-react';
import { toast } from 'sonner';

// Statuts des annonces selon le prompt
const ANNONCE_STATUSES = {
  en_ligne: { label: 'En ligne', color: 'bg-green-500 text-white', icon: Eye },
  en_attente: { label: 'En attente', color: 'bg-yellow-500 text-white', icon: Clock },
  brouillon: { label: 'Brouillon', color: 'bg-orange-500 text-white', icon: Edit },
  reservee: { label: 'Réservée', color: 'bg-blue-500 text-white', icon: ShoppingCart },
  vendue: { label: 'Vendue', color: 'bg-emerald-600 text-white', icon: CheckCircle },
  archivee: { label: 'Archivée', color: 'bg-gray-500 text-white', icon: Archive },
  refusee: { label: 'Refusée', color: 'bg-red-500 text-white', icon: XCircle },
};

const LISTING_TYPES = {
  neuf: { label: 'Neuf', color: 'bg-blue-100 text-blue-800' },
  occasion: { label: 'Occasion', color: 'bg-amber-100 text-amber-800' },
  reconditionne: { label: 'Reconditionné', color: 'bg-green-100 text-green-800' },
  location: { label: 'Location', color: 'bg-purple-100 text-purple-800' },
  service: { label: 'Service', color: 'bg-emerald-100 text-emerald-800' },
  travaux: { label: 'Travaux', color: 'bg-orange-100 text-orange-800' },
  etude: { label: 'Étude', color: 'bg-cyan-100 text-cyan-800' },
  talent: { label: 'Talent', color: 'bg-pink-100 text-pink-800' },
  presentation: { label: 'Présentation', color: 'bg-indigo-100 text-indigo-800' },
};

export default function AdminAnnonces() {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [listingTypeFilter, setListingTypeFilter] = useState('');
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [statusReason, setStatusReason] = useState('');

  useEffect(() => {
    fetchData();
    
    // Check if edit param exists
    const editId = searchParams.get('edit');
    if (editId) {
      openEditModal(editId);
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products/admin/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/categories`)
      ]);

      setProducts(productsRes.data.products || productsRes.data || []);
      setCategories(categoriesRes.data || []);
      
      // Calculate stats
      const allProducts = productsRes.data.products || productsRes.data || [];
      setStats({
        total: allProducts.length,
        en_ligne: allProducts.filter(p => p.status === 'en_ligne' || p.approval_status === 'approved').length,
        en_attente: allProducts.filter(p => p.status === 'en_attente' || p.approval_status === 'pending').length,
        brouillon: allProducts.filter(p => p.status === 'brouillon' || p.status === 'draft').length,
        vedette: allProducts.filter(p => p.featured).length,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProduct(response.data);
      setEditForm(response.data);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Erreur lors du chargement du produit');
    }
  };

  const updateProductStatus = async (productId, newStatus, reason = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/products/admin/${productId}/status`, 
        { status: newStatus, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Statut mis à jour : ${ANNONCE_STATUSES[newStatus]?.label || newStatus}`);
      fetchData();
      setShowStatusModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleFeatured = async (productId, currentFeatured) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/products/admin/${productId}/featured`, 
        { featured: !currentFeatured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(currentFeatured ? 'Retiré des vedettes' : 'Ajouté aux vedettes');
      fetchData();
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Erreur');
    }
  };

  const duplicateProduct = async (product) => {
    try {
      const token = localStorage.getItem('token');
      const duplicated = {
        ...product,
        name: `${product.name} (copie)`,
        name_fr: `${product.name_fr || product.name} (copie)`,
        status: 'brouillon',
        approval_status: 'pending',
        featured: false,
      };
      delete duplicated.id;
      delete duplicated._id;
      delete duplicated.created_at;
      
      await axios.post(`${API}/products/admin/create`, duplicated, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annonce dupliquée en brouillon');
      fetchData();
    } catch (error) {
      console.error('Error duplicating:', error);
      toast.error('Erreur lors de la duplication');
    }
  };

  const deleteProduct = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/admin/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annonce archivée (corbeille 30 jours)');
      fetchData();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const saveProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/products/admin/${editForm.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annonce mise à jour');
      fetchData();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const bulkAction = async (action) => {
    if (selectedProducts.length === 0) {
      toast.error('Sélectionnez au moins une annonce');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      for (const productId of selectedProducts) {
        if (action === 'archive') {
          await axios.put(`${API}/products/admin/${productId}/status`, 
            { status: 'archivee' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else if (action === 'en_attente') {
          await axios.put(`${API}/products/admin/${productId}/status`, 
            { status: 'en_attente' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else if (action === 'en_ligne') {
          await axios.put(`${API}/products/admin/${productId}/status`, 
            { status: 'en_ligne' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }
      toast.success(`${selectedProducts.length} annonces mises à jour`);
      setSelectedProducts([]);
      fetchData();
    } catch (error) {
      console.error('Error bulk action:', error);
      toast.error('Erreur lors de l\'action en masse');
    }
  };

  // Filter products
  const filteredProducts = products.filter(p => {
    if (searchTerm && !p.name?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !p.name_fr?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (statusFilter) {
      const productStatus = p.status || (p.approval_status === 'approved' ? 'en_ligne' : 'en_attente');
      if (productStatus !== statusFilter) return false;
    }
    if (categoryFilter && p.category_id !== categoryFilter) return false;
    if (listingTypeFilter && p.listing_type !== listingTypeFilter) return false;
    if (sourceFilter === 'geots' && !p.managed_by_geots) return false;
    if (sourceFilter === 'user' && p.managed_by_geots) return false;
    return true;
  });

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name_fr || cat?.name || 'Non catégorisé';
  };

  const getProductStatus = (product) => {
    if (product.status && ANNONCE_STATUSES[product.status]) {
      return product.status;
    }
    if (product.approval_status === 'approved') return 'en_ligne';
    if (product.approval_status === 'pending') return 'en_attente';
    if (product.approval_status === 'rejected') return 'refusee';
    return 'brouillon';
  };

  const formatPrice = (product) => {
    const price = product.price_used || product.price_new || product.price_rental || 0;
    if (price === 0) return 'Sur demande';
    return `${price.toLocaleString()} €`;
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50" data-testid="admin-annonces-page">
        <div className="p-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#0A2540]">Gestion des Annonces</h1>
              <p className="text-gray-500">Créez, modifiez et gérez toutes les annonces</p>
            </div>
            <div className="flex gap-3 mt-4 md:mt-0">
              <Button 
                onClick={() => navigate('/admin/products/new')}
                className="bg-[#635BFF] hover:bg-[#635BFF]/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle annonce
              </Button>
              <Button variant="outline">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('')}>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md border-l-4 border-l-green-500" onClick={() => setStatusFilter('en_ligne')}>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">En ligne</p>
                  <p className="text-2xl font-bold text-green-600">{stats.en_ligne}</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md border-l-4 border-l-yellow-500" onClick={() => setStatusFilter('en_attente')}>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.en_attente}</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md border-l-4 border-l-orange-500" onClick={() => setStatusFilter('brouillon')}>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">Brouillons</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.brouillon}</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md border-l-4 border-l-amber-500" onClick={() => { setStatusFilter(''); }}>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">Vedettes</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.vedette}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Rechercher par nom..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 rounded-lg border bg-white min-w-[150px]"
                >
                  <option value="">Tous les statuts</option>
                  {Object.entries(ANNONCE_STATUSES).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-2 rounded-lg border bg-white min-w-[150px]"
                >
                  <option value="">Toutes catégories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name_fr || cat.name}</option>
                  ))}
                </select>
                <select
                  value={listingTypeFilter}
                  onChange={(e) => setListingTypeFilter(e.target.value)}
                  className="px-4 py-2 rounded-lg border bg-white min-w-[120px]"
                >
                  <option value="">Tout type</option>
                  {Object.entries(LISTING_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="px-4 py-2 rounded-lg border bg-white min-w-[120px]"
                >
                  <option value="">Toute source</option>
                  <option value="geots">Géré GEOTS</option>
                  <option value="user">Vendeur</option>
                </select>
                <Button variant="outline" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {/* Bulk Actions */}
              {selectedProducts.length > 0 && (
                <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                  <span className="text-sm text-gray-600">{selectedProducts.length} sélectionnée(s)</span>
                  <Button size="sm" variant="outline" onClick={() => bulkAction('en_ligne')}>
                    <Eye className="w-4 h-4 mr-1" /> Mettre en ligne
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => bulkAction('en_attente')}>
                    <Pause className="w-4 h-4 mr-1" /> En attente
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => bulkAction('archive')}>
                    <Archive className="w-4 h-4 mr-1" /> Archiver
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedProducts([])}>
                    Désélectionner
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Products Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#635BFF]" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <Card className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune annonce trouvée</p>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="p-4 text-left">
                        <input
                          type="checkbox"
                          checked={selectedProducts.length === filteredProducts.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProducts(filteredProducts.map(p => p.id));
                            } else {
                              setSelectedProducts([]);
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">Photo</th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">Titre</th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">Catégorie</th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">Prix</th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">Type</th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">Statut</th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product) => {
                      const status = getProductStatus(product);
                      const StatusConfig = ANNONCE_STATUSES[status] || ANNONCE_STATUSES.brouillon;
                      const StatusIcon = StatusConfig.icon;
                      const listingType = LISTING_TYPES[product.listing_type];
                      
                      return (
                        <tr key={product.id} className="border-b hover:bg-gray-50">
                          <td className="p-4">
                            <input
                              type="checkbox"
                              checked={selectedProducts.includes(product.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProducts([...selectedProducts, product.id]);
                                } else {
                                  setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                                }
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className="p-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                              {product.images?.[0] ? (
                                <img 
                                  src={product.images[0]} 
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Image className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-[#0A2540]">{product.name_fr || product.name}</p>
                              <p className="text-xs text-gray-500">{product.brand} {product.model}</p>
                              {product.featured && (
                                <Badge className="bg-amber-100 text-amber-800 mt-1">
                                  <Star className="w-3 h-3 mr-1" /> Vedette
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-sm">{getCategoryName(product.category_id)}</span>
                          </td>
                          <td className="p-4">
                            <span className="font-semibold text-[#635BFF]">{formatPrice(product)}</span>
                          </td>
                          <td className="p-4">
                            {listingType && (
                              <Badge className={listingType.color}>{listingType.label}</Badge>
                            )}
                          </td>
                          <td className="p-4">
                            <Badge className={StatusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {StatusConfig.label}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => openEditModal(product.id)}
                                title="Modifier"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => toggleFeatured(product.id, product.featured)}
                                title={product.featured ? "Retirer vedette" : "Mettre en vedette"}
                              >
                                {product.featured ? <StarOff className="w-4 h-4 text-amber-500" /> : <Star className="w-4 h-4" />}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => { setSelectedProduct(product); setShowStatusModal(true); }}
                                title="Changer statut"
                              >
                                <Clock className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => duplicateProduct(product)}
                                title="Dupliquer"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => { setSelectedProduct(product); setShowDeleteModal(true); }}
                                title="Supprimer"
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>

        {/* Status Change Modal */}
        {showStatusModal && selectedProduct && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowStatusModal(false)}>
            <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold mb-4">Changer le statut</h3>
              <p className="text-sm text-gray-600 mb-4">Annonce : {selectedProduct.name_fr || selectedProduct.name}</p>
              
              <div className="grid grid-cols-2 gap-2 mb-4">
                {Object.entries(ANNONCE_STATUSES).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => updateProductStatus(selectedProduct.id, key)}
                      className={`p-3 rounded-lg border text-left hover:shadow-md transition-all ${
                        getProductStatus(selectedProduct) === key ? 'ring-2 ring-[#635BFF]' : ''
                      }`}
                    >
                      <Badge className={config.color + ' mb-2'}>
                        <Icon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Badge>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowStatusModal(false)}>Annuler</Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedProduct && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
            <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold mb-4 text-red-600">Supprimer l'annonce ?</h3>
              <p className="text-sm text-gray-600 mb-4">
                L'annonce "{selectedProduct.name_fr || selectedProduct.name}" sera archivée pendant 30 jours avant suppression définitive.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Annuler</Button>
                <Button 
                  className="bg-red-500 hover:bg-red-600"
                  onClick={() => deleteProduct(selectedProduct.id)}
                >
                  Supprimer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowEditModal(false)}>
            <div className="bg-white rounded-2xl max-w-3xl w-full my-8" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-lg font-bold">Modifier l'annonce</h3>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom (FR)</label>
                    <Input
                      value={editForm.name_fr || ''}
                      onChange={(e) => setEditForm({...editForm, name_fr: e.target.value, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Marque</label>
                    <Input
                      value={editForm.brand || ''}
                      onChange={(e) => setEditForm({...editForm, brand: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Modèle</label>
                    <Input
                      value={editForm.model || ''}
                      onChange={(e) => setEditForm({...editForm, model: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      value={editForm.listing_type || ''}
                      onChange={(e) => setEditForm({...editForm, listing_type: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Sélectionner</option>
                      {Object.entries(LISTING_TYPES).map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Prix occasion (€)</label>
                    <Input
                      type="number"
                      value={editForm.price_used || ''}
                      onChange={(e) => setEditForm({...editForm, price_used: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Prix neuf (€)</label>
                    <Input
                      type="number"
                      value={editForm.price_new || ''}
                      onChange={(e) => setEditForm({...editForm, price_new: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Catégorie</label>
                    <select
                      value={editForm.category_id || ''}
                      onChange={(e) => setEditForm({...editForm, category_id: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Sélectionner</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name_fr || cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Localisation</label>
                    <Input
                      value={editForm.location || ''}
                      onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={editForm.description_fr || editForm.description || ''}
                    onChange={(e) => setEditForm({...editForm, description_fr: e.target.value, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg h-32"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Notes admin (internes)</label>
                  <textarea
                    value={editForm.admin_notes || ''}
                    onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg h-20 bg-yellow-50"
                    placeholder="Notes visibles uniquement par l'admin..."
                  />
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-2 rounded-b-2xl">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
                <Button onClick={saveProduct} className="bg-[#635BFF] hover:bg-[#635BFF]/90">
                  Sauvegarder
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
